/**
 * Data layer behind the "Funds by Direct Agent/Investor" table (PT-5714).
 *
 * The table is an agent's daily working view of who sits directly beneath them: each direct child
 * offer as one row, largest contributor first. Every figure is a pure function of the OfferNode
 * already in memory — the direct children's precomputed fields — so the table issues no network
 * call and stays consistent with the tree it is rendered inside.
 *
 * Share % is deliberately computed against the table's OWN total (the sum of the direct children's
 * funds), not the node's AUM, so the column always totals 100% (±rounding).
 *
 * The table's funds total does NOT reconcile to the node's Total Funds tile, and is not meant to
 * (PT-5715). Total Funds is the node's own AUM — the balances of the investors in ITS book — whereas
 * this table sums the AUM of its child sub-offers, which is the direct-children slice of the node's
 * Downline Funds. They are two different axes (a node's own book vs. what its downline holds), so the
 * figure that this total reconciles against is Downline Funds, never Total Funds.
 */
import {OfferNode} from '../types'
import {getNodeRole, NodeRole} from './kpiGrid'

/** One row of the contributions table: a single direct child offer. */
export type ContributionRow = {
  /** The child offer's id as a string, used as the scroll anchor; `null` when the offer has no id. */
  offerId: string | null
  /** Who owns the child offer (manager name), falling back to the offer name. */
  ownerName: string
  /** The child's role in the tree — a distributing `agent` or a leaf `investor` position. */
  role: NodeRole
  /** The child's current funds (AUM); a missing value counts as 0 so the row still totals. */
  funds: number
  /** The child's share of the table's total funds in `[0, 1]`; `null` when there are no funds to split. */
  share: number | null
  /** Number of investors in the child offer. */
  clients: number
}

/** Which role the direct children collectively are, driving the table's heading. */
export type ContributionsKind = 'agents' | 'investors' | 'mixed'

/**
 * Orders rows by funds descending (AC: "Default sort: Funds descending"), with the child offer id
 * as a stable tiebreak — matching the numeric id order the tree already sorts children into — so
 * equal-funds rows never jitter between renders. Rows with no id sink last.
 */
const byFundsDesc = (a: ContributionRow, b: ContributionRow): number => {
  if (b.funds !== a.funds) return b.funds - a.funds
  const aId = a.offerId != null ? Number(a.offerId) : Infinity
  const bId = b.offerId != null ? Number(b.offerId) : Infinity
  // Two id-less rows compare equal — `Infinity - Infinity` is NaN, an invalid comparator result.
  if (aId === bId) return 0
  return aId - bId
}

/**
 * Builds the contributions rows for a node from its direct children, sorted funds-descending.
 *
 * @param node - The node whose direct children populate the table.
 * @returns One row per direct child, largest contributor first.
 */
export const buildContributionRows = (node: OfferNode): ContributionRow[] => {
  const totalFunds = node.children.reduce((sum, child) => sum + (child.offer.aum ?? 0), 0)
  return node.children
    .map((child): ContributionRow => {
      const funds = child.offer.aum ?? 0
      return {
        offerId: child.offer.id != null ? String(child.offer.id) : null,
        ownerName: child.offer.managerName || child.offer.name || '',
        role: getNodeRole(child),
        funds,
        // Only defined when there are funds to split; zero total means every share is unknown, never a bogus 0%.
        share: totalFunds > 0 ? funds / totalFunds : null,
        clients: child.offer.totalInvestments ?? 0,
      }
    })
    .sort(byFundsDesc)
}

/**
 * Classifies the children collectively so the table heading can adapt (AC: "the header label adapts
 * to the children's role — Agents vs Investors vs mixed"). Any non-investor child (agent, or the
 * defensive master case) counts as an agent for the heading.
 *
 * @param rows - The contribution rows.
 * @returns `agents`, `investors`, or `mixed`.
 */
export const contributionsKind = (rows: ContributionRow[]): ContributionsKind => {
  const hasInvestors = rows.some((row) => row.role === 'investor')
  const hasAgents = rows.some((row) => row.role !== 'investor')
  if (hasAgents && hasInvestors) return 'mixed'
  return hasInvestors ? 'investors' : 'agents'
}
